<script>
import { mapState } from 'vuex';
import firebase from 'firebase/app';
import moment from 'moment';
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';
import GalleryShareDlg from "@/views/staff/gallery/GalleryShareDlg/GalleryShareDlg.vue";
import Loader from '@/components/loader/Loader.vue';

export default {
  name: 'CanvaPhotos',
  components: {
    Loader,
    ConfirmDlg,
    GalleryShareDlg
  },
  data() {
    return {
      picker: {
        start: false,
        end: false,
      },
      startDate: moment().subtract(1, 'month').toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      loading: false,
      photos: []
    };
  },

  computed: {
    ...mapState({
      facility: state => state.facilityModule.selectedFacility,
    }),
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat);
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat);
    },
  },

  created() {
      this.getCanvaPhotos()
  },

  methods: {
    async getCanvaPhotos() {
      this.loading = true;
      const startDate = moment(this.startDate).startOf('day').unix() * 1000;
      const endDate = moment(this.endDate).endOf('day').unix() * 1000;

      const storageRef = firebase.storage().ref();
      const folderRef = storageRef.child(`${this.facility.id}/photos/canva`);

      const listResult = await folderRef.listAll();
      const urlPromises = listResult.items.map(async itemRef => {
        const itemName = itemRef.name;
        if (!itemName.includes('200x200')) {
          const url = await itemRef.getDownloadURL();
          const metadata = await itemRef.getMetadata();
          const createdAt = moment(metadata.timeCreated).startOf('day').unix() * 1000;
          if (createdAt >= startDate && createdAt <= endDate) {
            return { id: itemRef.name, image: url, createdAt };
          } else {
            return null;
          }
        } else {
          return Promise.resolve(null);
        }
      });
      const canvasPhotos = await Promise.all(urlPromises);
      const filteredCanvasPhotos = canvasPhotos.filter(photo => photo !== null);
      this.photos = filteredCanvasPhotos.sort((a, b) => a.createdAt - b.createdAt);
      this.loading = false;
    },

    openDialog(event, photo) {
      if (event.target.className.includes('selection-controls')) return;

      this.$refs.galleryShareDlg.open(photo);
    },
  },
}
</script>

<template>
  <v-app>
    <div>
      <v-card class="px-5 pb-5">
        <v-card-title class="pl-0 pr-0 align-start">
          <v-col cols="2" class="pl-0">
            <router-link class="mr-3 text-decoration-none" icon to="/gallery">
              <v-icon>mdi-arrow-left</v-icon>
            </router-link>

            {{ $t('Gallery.CanvaPhotos') }}
          </v-col>

          <v-col cols="8" class="d-flex justify-content-end pb-0">
            <v-flex lg12 md12 xs12 class="pl-0">
              <v-row ma-5 class="justify-content-end">
                <v-col cols="5">
                  <v-menu
                    v-model="picker.start"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedStartDate"
                        :label="$t('GeneralTranslations.StartDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      :locale="$i18n.locale"
                      v-model="startDate"
                      @input="picker.start = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="5">
                  <v-menu
                    v-model="picker.end"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedEndDate"
                        :label="$t('GeneralTranslations.EndDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      >
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :locale="$i18n.locale"
                      v-model="endDate"
                      @input="picker.end = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="2">
                  <v-btn class="primary mr-3" @click="getCanvaPhotos">{{ $t('GeneralTranslations.Update') }}</v-btn>
                </v-col>
              </v-row>
            </v-flex>
          </v-col>
        </v-card-title>

        <v-row>
          <v-col v-for="photo in photos" :key="photo.id" class="d-flex child-flex" cols="2">
            <v-card>
              <v-img
                :src="photo.image"
                :key="photo.id"
                aspect-ratio="1"
                class="grey lighten-2"
                v-once
                @click="openDialog($event, photo)"
              >
              </v-img>
              <v-card-title class="">
                <v-col>
                  <v-row style="word-break: break-word;">
                    <b class="mr-1">{{ $t('Gallery.Date') }}: </b>{{photo.createdAt | localizedDate}}
                  </v-row>
                </v-col>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-card>

      <Loader v-if="loading"></Loader>

      <ConfirmDlg ref="confirmationModal"></ConfirmDlg>

      <GalleryShareDlg
        :showOptions="false"
        ref="galleryShareDlg"
      />
    </div>
  </v-app>
</template>
<style src="./GalleryCanvaPhotos.scss" lang="scss" scoped></style>